import {makeAutoObservable, runInAction} from "mobx";
import {IChangeable, ISaveable} from "../../../stores/types";
import {IMerchantOnboarding, IMerchantOnboardingSettings} from "../onboarding-types";
import {rootContainer, TYPE} from "../../../services/container/root";
import OnboardingService from "../../../services/api/onboarding-service";
import {ONBOARDING_STEPS, PLAN_TYPE} from "../constants";
import WelcomeScreenStore from "./welcome-screen-store";
import ChoosePlanScreenStore from "./choose-plan-screen-store";
import ChoosePlanTypeScreenStore from "./choose-plan-type-screen-store";
import ChooseResolutionScreenStore from "./choose-resolution-screen-store";
import ApprovePlanScreenStore from "./approve-plan-screen-store";
import FinalScreenStore from "./final-screen-store";
import {OnboardingNavigationStore} from "./onboarding-navigation-store";
import EnableReturnGuardstore from "./enable-return-guard-store";
import SetupReturnGuardStore from "./setup-return-guard-store";
import CreateReturnMethodScreenStore from "./create-return-methods-store";
import WalletConfigurationStore from "./wallet-configuration-store";
import onboardingPlansStores from "./onboarding-plans-stores";
import {isSet} from "../../../models/model-types-methods";
import onboardingMixPanelStore, {ONBOARDING_MIXPANEL_EVENT} from "./onboarding-mixpanel-store";

export type IGetMerchantOnboardingModel = () => IMerchantOnboarding | undefined;

export interface IOnboardingScreenStore extends IChangeable<IMerchantOnboarding>  {
    // Common properties/methods that ALL onboarding screen stores should have
    isComplete(): boolean;
    getMixPanelData(): Record<string, any>;
}

export interface IMerchantOnboardingChanges extends Partial<IMerchantOnboarding> {};

export interface IOnboardingSaveable extends Omit<ISaveable, "save"> {
    save(stepName: ONBOARDING_STEPS): Promise<boolean>;
}

class OnboardingDataStore implements IOnboardingSaveable {
    private _isLoading: boolean = false;
    private _isSaving: boolean = false;
    private _model?: IMerchantOnboarding;
    private _settings?: IMerchantOnboardingSettings;

    public getModel(): IMerchantOnboarding | undefined { return this._model; }
    public getSetting(): IMerchantOnboardingSettings | undefined { return this._settings; }

    private _screenStores: Record<ONBOARDING_STEPS, IOnboardingScreenStore>;

    private get onboardingService(): OnboardingService {
        return rootContainer.get<OnboardingService>(TYPE.OnboardingService);
    }

    constructor(private _navigationStore: OnboardingNavigationStore) {
        makeAutoObservable(this, {}, { autoBind: true });
        // Always initialize screen stores
        this._screenStores = this._createScreenStores();
    }

    private _createScreenStores(): Record<ONBOARDING_STEPS, IOnboardingScreenStore> {
        return {
            [ONBOARDING_STEPS.NEW_USER_WELCOME]: new WelcomeScreenStore(this, this._navigationStore),
            [ONBOARDING_STEPS.CHOOSE_PLAN_TYPE]: new ChoosePlanTypeScreenStore(this, this._navigationStore),
            [ONBOARDING_STEPS.CHOOSE_PLAN]: new ChoosePlanScreenStore(this, this._navigationStore),
            [ONBOARDING_STEPS.CHOOSE_RETURN_TYPE]: new ChooseResolutionScreenStore(this, this._navigationStore),
            [ONBOARDING_STEPS.CREATE_RETURN_METHOD]: new CreateReturnMethodScreenStore(this, this._navigationStore),
            [ONBOARDING_STEPS.WALLET_CONFIGURATION]: new WalletConfigurationStore(this, this._navigationStore),
            [ONBOARDING_STEPS.ENABLE_RETURN_GUARD]: new EnableReturnGuardstore(this, this._navigationStore),
            [ONBOARDING_STEPS.SETUP_RETURN_GUARD]: new SetupReturnGuardStore(this, this._navigationStore),
            [ONBOARDING_STEPS.APPROVE_PLAN]: new ApprovePlanScreenStore(this, this._navigationStore), /** screen that redirects to shopify plan approval */
            [ONBOARDING_STEPS.FINAL]: new FinalScreenStore(this, this._navigationStore), /** after approval screen, responisible for starting the processor */
        };
    }

    private _setModel(model: IMerchantOnboarding | null, stepName?: ONBOARDING_STEPS): void {
        if (model) {
            this._model = model;
            onboardingMixPanelStore.setOnboardingModelId(model.id);
        }

        if (!stepName || stepName === ONBOARDING_STEPS.CREATE_RETURN_METHOD) {
            (this.getScreenStore(ONBOARDING_STEPS.CREATE_RETURN_METHOD) as CreateReturnMethodScreenStore).onSetModel();
        }

        Object.keys(this._screenStores).forEach(key => {
            if (key === ONBOARDING_STEPS.CREATE_RETURN_METHOD) {
                /** skip discard changed in RM store */
                return;
            }
            this._screenStores[key as ONBOARDING_STEPS].discardChanges();
        });
    }

    public get enableExtensionEmbedBlockURL(): string { return this._settings?.extension_embed_block_url ?? '#'; }
    public get addExtensionUIBlockURL(): string { return this._settings?.extension_ui_block_url ?? '#'; }

    public getScreenStore(stepName: ONBOARDING_STEPS): IOnboardingScreenStore {
        return this._screenStores[stepName];
    }

    public getPlanType(): PLAN_TYPE | undefined | null {
        const planTypeScreenStore = this.getScreenStore(ONBOARDING_STEPS.CHOOSE_PLAN_TYPE) as ChoosePlanTypeScreenStore;
        return planTypeScreenStore?.planType;
    }

    public isSelectedCoveragePlanId(): boolean {
        const choosePlanScreenStore = this.getScreenStore(ONBOARDING_STEPS.CHOOSE_PLAN) as ChoosePlanScreenStore;
        return choosePlanScreenStore.planId === onboardingPlansStores.onboardingCoveragePlan?.id;
    }

    public isPlanIdSelected(): boolean {
        const choosePlanScreenStore = this.getScreenStore(ONBOARDING_STEPS.CHOOSE_PLAN) as ChoosePlanScreenStore;
        return isSet(choosePlanScreenStore.planId);
    }

    public isSaving(): boolean {
        return this._isSaving;
    }

    public getUpdatedModel(stepName: ONBOARDING_STEPS): IMerchantOnboarding {
        return {
            ...this.getScreenStore(stepName)?.getUpdatedModel()
        } as IMerchantOnboarding;
    }

    /** handle changes from one store that might affect other store */
    private _handleBeforeStepSave(stepName: ONBOARDING_STEPS): void {
        /** when plan selection changed we want to uncheck all selections in resolutions screen */
        if ([ONBOARDING_STEPS.CHOOSE_PLAN, ONBOARDING_STEPS.CHOOSE_PLAN_TYPE].includes(stepName)) {
            const resolutionsStore = this.getScreenStore(ONBOARDING_STEPS.CHOOSE_RETURN_TYPE) as ChooseResolutionScreenStore;
            resolutionsStore.clearAllSelections();
        }
    }

    public async save(stepName: ONBOARDING_STEPS): Promise<boolean> {
        const store = this.getScreenStore(stepName);
        /** when navigating forward without makings changes - no need to save anything */
        if (!store.hasChanges()) {
            return true;
        }

        let isSuccess = true;
        try {
            this._handleBeforeStepSave(stepName);

            this._isSaving = true;
            const data = this.getUpdatedModel(stepName);
            const model = await this.onboardingService.saveOnboardingData(data);
            const mixPanelData = store.getMixPanelData();

            runInAction(() => {
                this._setModel(model, stepName);

                /** We need to put it here to have correct context for onboarding_id property of event, on welcome screen */
                onboardingMixPanelStore.trackEvent(ONBOARDING_MIXPANEL_EVENT.ONBOARDING_DATA_COLLECTED, {
                    from: stepName,
                    collected_data: {
                        ...mixPanelData
                    }
                });

                this._isSaving = false;
            });
        }
        catch (e) {
            runInAction(() => {
                this._isSaving = false;
            });
            isSuccess = false;
        }
        return isSuccess;
    }

    public async load(): Promise<void> {
        this._isLoading = true;

        try {
            const { onboarding: model, settings } = await this.onboardingService.getOnboardingData();
            runInAction(() => {
                onboardingMixPanelStore.setIdentity({
                    shop_id: settings.merchant.portal_id,
                    shop_name: settings.merchant.portal_name,
                    shop_company: settings.merchant.logged_in_user_company,
                    human_id: settings.merchant.logged_in_user_id,
                    human_email: settings.merchant.logged_in_user_email,
                    human_role: settings.merchant.logged_in_user_role,
                });

                onboardingMixPanelStore.startMixpanelSessionRecording();

                this._isLoading = false;
                this._setModel(model);
                this._settings = settings;
            });
        }
        catch (e) {
            runInAction(() => {
                this._isLoading = false;
            });
        }
    }
}

export default OnboardingDataStore;